<template>
  <main-template>
    <v-container>
      <v-row align="center" class="mb-1">
        <v-col>
          <h1>
            <v-btn color="primary" x-small text @click="$router.go(-1)">
              <v-icon left>fa fa-angle-left</v-icon>
            </v-btn>
            Configurações
          </h1>
        </v-col>
      </v-row>

      <v-card class="elevation-2">
        <v-card-title class="d-flex align-center flex-wrap">
          <span class="mr-4">Módulos por perfil</span>
          <v-spacer />
          <v-autocomplete
            v-if="isSuperAdmin"
            v-model="configuracaoId"
            :items="franquias"
            item-text="descricao"
            item-value="id"
            label="Franquia"
            dense
            outlined
            hide-details
            style="max-width: 360px"
            :disabled="salvando"
            @change="carregar"
          />
        </v-card-title>
        <v-card-subtitle class="pt-2">
          Marque para quais perfis cada módulo aparece nesta franquia. Um módulo desmarcado some do
          menu e da tela inicial e tem o acesso bloqueado para aquele perfil.
        </v-card-subtitle>

        <v-alert type="info" text dense class="mx-4 mb-0">
          O Super Admin sempre vê todos os módulos. Início, Colaboradores e Administração não podem
          ser ocultados.
        </v-alert>

        <v-card-text>
          <v-skeleton-loader v-if="carregando" type="table-tbody" />
          <v-simple-table v-else class="tabela-modulos">
            <thead>
              <tr>
                <th class="text-left">Módulo</th>
                <th v-for="perfil in perfis" :key="perfil.valor" class="text-center coluna-perfil">
                  {{ perfil.titulo }}
                </th>
              </tr>
            </thead>
            <tbody>
              <template v-for="grupo in grupos">
                <tr :key="`grupo-${grupo.nome}`" class="linha-grupo">
                  <td :colspan="perfis.length + 1" class="font-weight-bold">{{ grupo.nome }}</td>
                </tr>
                <tr v-for="modulo in grupo.modulos" :key="modulo.chave">
                  <td class="py-2">
                    <div class="font-weight-medium">{{ modulo.nome }}</div>
                    <div class="text-caption grey--text">{{ modulo.descricao }}</div>
                  </td>
                  <td v-for="perfil in perfis" :key="perfil.valor" class="text-center">
                    <v-simple-checkbox
                      v-if="modulo.perfis.includes(perfil.valor)"
                      :value="marcado(modulo.chave, perfil.valor)"
                      :disabled="salvando"
                      color="primary"
                      class="d-inline-flex"
                      @input="alternar(modulo.chave, perfil.valor, $event)"
                    />
                    <span v-else class="grey--text" title="Não se aplica a este perfil">—</span>
                  </td>
                </tr>
              </template>
            </tbody>
          </v-simple-table>
        </v-card-text>

        <v-divider />

        <v-card-actions class="flex-wrap">
          <v-btn text :disabled="carregando || salvando" @click="restaurarPadrao">
            <v-icon left>mdi-restore</v-icon>
            Restaurar padrão
          </v-btn>
          <v-spacer />
          <span v-if="alterado" class="text-caption grey--text mr-3">Alterações não salvas</span>
          <v-btn
            color="primary"
            :loading="salvando"
            :disabled="carregando || !alterado"
            @click="salvar"
          >
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-container>
  </main-template>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "ConfiguracoesModulosPage",
  data() {
    return {
      carregando: false,
      salvando: false,
      configuracaoId: null,
      catalogo: [],
      // chave do módulo => perfis liberados (como está na tela)
      configuracao: {},
      // último estado salvo, para saber se há alterações
      original: {},
      perfis: [
        { valor: "colaborador", titulo: "Colaborador" },
        { valor: "lider", titulo: "Líder" },
        { valor: "admin", titulo: "Admin" },
      ],
    };
  },
  computed: {
    ...mapGetters("Auth", ["user"]),
    ...mapGetters("Franquias", ["franquias"]),
    isSuperAdmin() {
      return this.user?.roleName === "super-admin";
    },
    grupos() {
      const grupos = [];
      this.catalogo.forEach((modulo) => {
        let grupo = grupos.find((g) => g.nome === modulo.grupo);
        if (!grupo) {
          grupo = { nome: modulo.grupo, modulos: [] };
          grupos.push(grupo);
        }
        grupo.modulos.push(modulo);
      });
      return grupos;
    },
    alterado() {
      return JSON.stringify(this.normalizar(this.configuracao)) !== JSON.stringify(this.original);
    },
  },
  mounted() {
    this.configuracaoId = this.user?.configuracao_id || this.user?.franquia?.id || null;
    this.carregar();
  },
  methods: {
    async carregar() {
      try {
        this.carregando = true;
        const data = await this.$services.appConfigurationService.getModulos(this.configuracaoId);
        this.aplicarResposta(data);
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.carregando = false;
      }
    },
    aplicarResposta(data) {
      this.configuracaoId = data.configuracao_id;
      if (data.catalogo) this.catalogo = data.catalogo;
      this.configuracao = this.copiar(data.configuracao);
      this.original = this.normalizar(data.configuracao);
    },
    marcado(chave, perfil) {
      return (this.configuracao[chave] || []).includes(perfil);
    },
    alternar(chave, perfil, valor) {
      const atuais = (this.configuracao[chave] || []).filter((p) => p !== perfil);
      this.$set(this.configuracao, chave, valor ? [...atuais, perfil] : atuais);
    },
    restaurarPadrao() {
      const padrao = {};
      this.catalogo.forEach((modulo) => {
        padrao[modulo.chave] = [...modulo.perfis];
      });
      this.configuracao = padrao;
    },
    async salvar() {
      try {
        this.salvando = true;
        const data = await this.$services.appConfigurationService.salvarModulos({
          configuracao_id: this.configuracaoId,
          modulos: this.configuracao,
        });
        this.aplicarResposta(data);
        // Atualiza menu e rotas do próprio usuário na hora.
        await this.$store.dispatch("AppConfigurations/fetchModulos");
        this.$toast.success("Configuração dos módulos salva.");
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.salvando = false;
      }
    },
    copiar(configuracao) {
      const copia = {};
      Object.keys(configuracao || {}).forEach((chave) => {
        copia[chave] = [...configuracao[chave]];
      });
      return copia;
    },
    // Mesma ordem de chaves e de perfis, para comparar alterações.
    normalizar(configuracao) {
      const ordem = this.perfis.map((p) => p.valor);
      const normal = {};
      Object.keys(configuracao || {})
        .sort()
        .forEach((chave) => {
          normal[chave] = ordem.filter((p) => configuracao[chave].includes(p));
        });
      return normal;
    },
  },
};
</script>

<style scoped>
.coluna-perfil {
  width: 120px;
}

.linha-grupo td {
  background: var(--v-primary-base, #4f008f);
  background: rgba(79, 0, 143, 0.06);
}

.tabela-modulos >>> tbody tr:hover {
  background: transparent !important;
}
</style>
